import { Order, OrderStatus } from '@api/Orders/Order';
import { DeliveryType } from '@api/Orders/Order';
import { dateTime, describeChanges, FieldSpecs, money, quoted } from '@api/ActivityLogs/DescribeChanges';
import { ActivityOrigin, buildActivityOrigin } from '@api/ActivityLogs/ActivityOrigin';
import { getOrderPaidBy, getOrderPayments, OrderPayment, PaymentMethod } from '@api/Orders/OrderPayments';

/**
 * Qué se audita de un pedido cuando alguien lo edita A MANO, y cómo se lee cada
 * campo en la bitácora (M1 del `PLAN-BITACORA-ACTIVITY-LOGS.md`).
 *
 * Por qué existe: `OrderServ.updateOrder` es la escritura PLANA del pedido —la
 * que usa `ModalViewOrder` para corregir montos y fechas, y el "regresar un
 * paso" del tablero— y **no registraba absolutamente nada**. Un admin podía
 * cambiar el total de una venta, o mover su fecha (lo que la cambia de corte de
 * caja y de informe), sin dejar rastro de quién ni de qué había antes. Es el
 * equivalente, en pedidos, del cambio de precio que este plan vino a resolver.
 *
 * ⚠️ **Costo**: una escritura por edición REAL —si nada cambió no se escribe
 * nada—. Editar un pedido a mano es cosa de unas pocas al día, y "regresar un
 * paso" otras tantas; contra las ~1,456 escrituras diarias que ya aportan los
 * siete eventos de pedidos, es despreciable.
 */

/** Los estados, como los diría una persona. La bitácora la lee gente, no el sistema. */
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
    [OrderStatus.WaitingForPayment]: 'En espera de pago',
    [OrderStatus.Preparing]: 'En preparación',
    [OrderStatus.Paid]: 'Pagado',
    [OrderStatus.Ready]: 'Listo',
    [OrderStatus.Route]: 'En ruta',
    [OrderStatus.Completed]: 'Completado',
    [OrderStatus.Cancelled]: 'Cancelado'
};

export const getOrderStatusLabel = (status?: unknown): string =>
    ORDER_STATUS_LABELS[status as OrderStatus] || String(status ?? 'sin estado');

const DELIVERY_LABELS: Record<string, string> = {
    [DeliveryType.Delivery]: 'Envío a domicilio',
    [DeliveryType.Pickup]: 'Recolección en sucursal'
};

const getDeliveryLabel = (value?: unknown): string => DELIVERY_LABELS[String(value)] || String(value ?? 'sin definir');

/**
 * ⚠️ **`products` NO está aquí y es a propósito**: comparar un arreglo de líneas
 * campo por campo daría una frase ilegible ("agregó [object Object]"). El cambio
 * de productos se detecta aparte, en `describeOrderChanges`, y se resume en una
 * sola frase. Lo que el dinero refleje de ese cambio (subtotal, total) sí sale
 * detallado, que es lo que se audita.
 */
/**
 * Lo cobrado por medio, LEÍDO de `payments[]` (`getOrderPaidBy`): es lo que corrige
 * `ModalViewOrder`. Hasta la F6 del plan de pagos se describía con los 3 escalares
 * del documento; hoy esos campos ya no existen en las órdenes nuevas.
 */
interface PaidView {
    paidCash: number;
    paidCard: number;
    paidTransfer: number;
}

const withPaidView = (order: Partial<Order>): Partial<Order> & PaidView => ({
    ...order,
    paidCash: getOrderPaidBy(order, PaymentMethod.Cash),
    paidCard: getOrderPaidBy(order, PaymentMethod.CreditCard),
    paidTransfer: getOrderPaidBy(order, PaymentMethod.BankTransfer)
});

export const ORDER_ACTIVITY_FIELDS: FieldSpecs<Order & PaidView> = {
    status: { label: 'el estado', format: getOrderStatusLabel },
    deliveryType: { label: 'el tipo de entrega', format: getDeliveryLabel },
    note: { label: 'la nota', format: quoted },
    subtotal: { label: 'el subtotal', format: money },
    promotion: { label: 'la promoción', format: money },
    shippingCost: { label: 'el envío', format: money },
    total: { label: 'el total', format: money },
    pointsAmount: { label: 'el pago con puntos', format: money },
    paidCash: { label: 'el efectivo', format: money },
    paidCard: { label: 'la tarjeta', format: money },
    paidTransfer: { label: 'la transferencia', format: money },
    tipAmount: { label: 'la propina', format: money },
    routeUrl: { label: 'el link de seguimiento', format: quoted },
    // Mover la fecha cambia el pedido de corte de caja y de informe de ventas:
    // es la edición más delicada que ofrece el modal, y la que más rastro merece.
    date: { label: 'la fecha del pedido', format: dateTime },
    pickupDate: { label: 'la hora de recolección', format: dateTime },
    reservationDate: { label: 'la hora de la reserva', format: dateTime },
    isReservation: {
        label: 'la reserva',
        verbs: { on: 'marcó el pedido como reserva', off: 'quitó la marca de reserva' }
    }
};

/** Huella de las líneas, para saber si el pedido cambió de contenido. */
const getProductsFingerprint = (order?: Partial<Order> | null): string => {
    try {
        return JSON.stringify(order?.products ?? []);
    } catch {
        return '';
    }
};

/**
 * La frase para la bitácora, o `''` si no cambió nada — y `''` significa **no
 * escribir el log**: abrir un pedido y guardarlo sin tocarlo no es un evento.
 */
export const describeOrderChanges = (before?: Partial<Order> | null, after?: Partial<Order> | null): string => {
    if (!before || !after) return '';
    const frases: string[] = [];
    if (getProductsFingerprint(before) !== getProductsFingerprint(after)) {
        frases.push('cambió los productos del pedido');
    }
    const resto = describeChanges(
        withPaidView(before) as Order & PaidView,
        withPaidView(after) as Order & PaidView,
        ORDER_ACTIVITY_FIELDS
    );
    if (resto) frases.push(resto);
    return frases.join(', ');
};

/**
 * ¿Este pedido nació en el admin (POS)? Importa para el renglón derivado de la
 * bitácora (D8): **solo el admin escribe `activity-logs`**, así que un pedido del
 * POS ya trae su *"creó este pedido"* y derivarlo otra vez sería duplicarlo (D6),
 * mientras que **un pedido de la WEB —checkout, kiosco o mesa QR— no tiene ningún
 * rastro de su creación**: su bitácora empieza en el primer clic que alguien le
 * dio desde el admin, o está vacía.
 *
 * Se decide con `createdWith` (lo escribe quien crea la orden desde 2026-08-27) y,
 * para las históricas que no lo traen, con **`collaboratorId`: la web nunca lo
 * escribe** — el mismo discriminador que usó el backfill de `payments[]`.
 */
export const wasOrderCreatedInAdmin = (order?: Partial<Order> | null): boolean => {
    if (!order) return false;
    const app = order.createdWith?.app;
    if (app) return app === 'admin';
    return !!order.collaboratorId;
};

const PAYMENT_LABELS: Record<string, string> = {
    [PaymentMethod.Cash]: 'en efectivo',
    [PaymentMethod.CreditCard]: 'con tarjeta',
    [PaymentMethod.BankTransfer]: 'por transferencia'
};

/** Un pago que NO cobró una persona en el POS: lo cobró la web por Stripe. */
const esPagoAutomatico = (payment: OrderPayment): boolean => !payment.collaboratorId;

/**
 * La historia del pedido que **el propio documento ya conoce**, para pintarla en
 * la bitácora sin escribir ni leer nada (extensión de D8, decidida con Irving el
 * 2026-09-10).
 *
 * El problema que resuelve, medido en producción: **solo el admin escribe
 * `activity-logs`**, así que un pedido de la web abre su bitácora con **cero o un
 * renglón** —uno completado mostraba únicamente *"actualizó el pedido a Listo"*—
 * mientras que uno del POS trae dos o tres. Que el cliente lo creó, que Stripe lo
 * cobró y que Uber lo entregó **no aparece por ningún lado**, aunque los tres
 * dejaron su marca en el documento.
 *
 * La alternativa era que la web y el server escribieran bitácora. Se descartó: ahí
 * **no hay un "quién"** (el actor sería un webhook — el código muerto del server
 * llegaba a firmar como `"Mercadopago"`), sería un módulo de paridad ×3 más, y
 * metería una escritura en el camino crítico del webhook, que es el que cobra y
 * otorga los puntos. **El rastro ya existe: lo que faltaba era mostrarlo.**
 */
export const buildOrderTimeline = (order?: Order | null): ActivityOrigin[] => {
    if (!order) return [];
    const entradas: (ActivityOrigin | undefined)[] = [];

    // La creación, solo si NO nació en el admin: los del POS ya la registran.
    if (!wasOrderCreatedInAdmin(order)) {
        entradas.push(buildActivityOrigin(order, { noun: 'Pedido recibido' }));
    }

    // El cobro de la web. Los del POS los cobró una persona y ya dejaron su
    // renglón ("realizó el pago de este pedido").
    getOrderPayments(order).filter(esPagoAutomatico).forEach((payment) => {
        const metodo = PAYMENT_LABELS[payment.method] || '';
        const porStripe = payment.reference?.startsWith('pi_') ? ' (Stripe)' : '';
        entradas.push(
            buildActivityOrigin(
                { date: payment.date },
                { noun: `Pagado ${metodo} por el cliente${porStripe}`.replace('  ', ' ') }
            )
        );
    });

    // Uber cierra el pedido solo. Sin `uberDeliveredAt` no hay renglón: no se
    // inventa una fecha (misma regla que el resto de los derivados).
    if (order.uberDeliveredAt) {
        entradas.push(buildActivityOrigin({ date: order.uberDeliveredAt }, { noun: 'Entregado por Uber' }));
    }

    return entradas.filter((entrada): entrada is ActivityOrigin => !!entrada);
};
